/**
 * Zitadel OIDC Configuration
 *
 * This module configures the oidc-client-ts UserManager for authentication
 * with Zitadel using Authorization Code + PKCE flow.
 *
 * Environment Variables:
 * - VITE_ZITADEL_ISSUER: Zitadel instance URL (e.g., http://localhost:8081)
 * - VITE_ZITADEL_CLIENT_ID: OIDC application client ID from Zitadel Console
 * - VITE_APP_URL: Application base URL (e.g., http://localhost:3020)
 */

import { UserManager, WebStorageStateStore, Log } from 'oidc-client-ts';

/**
 * Creates OIDC Configuration for Zitadel
 *
 * Uses Authorization Code flow with PKCE (Proof Key for Code Exchange)
 * which is the recommended flow for SPAs and native apps.
 *
 * NOTE: This function must only be called on the client side (browser)
 * because it uses WebStorageStateStore which requires localStorage.
 */
function createConfig() {
  // Enable debug logging in development (only in browser)
  if (process.env.NODE_ENV === 'development') {
    Log.setLogger(console);
    Log.setLevel(Log.DEBUG);
  }

  return {
    // Zitadel instance URL
    authority: import.meta.env.VITE_ZITADEL_ISSUER || 'http://localhost:8081',

    // Client ID from Zitadel Console (OIDC Application)
    client_id: import.meta.env.VITE_ZITADEL_CLIENT_ID || '',

    // Where to redirect after login
    redirect_uri: `${import.meta.env.VITE_APP_URL || 'http://localhost:3020'}/auth/callback`,

    // Where to redirect after logout
    post_logout_redirect_uri:
      import.meta.env.VITE_APP_URL || 'http://localhost:3020',

    // Response type for Authorization Code + PKCE flow
    response_type: 'code',

    // Scopes requested from Zitadel
    // - openid: Required for OIDC
    // - profile: Basic profile info (name, etc.)
    // - email: Email address
    // - offline_access: Enables refresh tokens for session persistence
    // - urn:zitadel:iam:org:project:id:{projectId}:aud: Include project in audience (required for role claims)
    // - urn:zitadel:iam:org:projects:roles: Include project role claims in token
    scope: [
      'openid',
      'profile',
      'email',
      'offline_access',
      `urn:zitadel:iam:org:project:id:${import.meta.env.VITE_ZITADEL_PROJECT_ID}:aud`,
      'urn:zitadel:iam:org:projects:roles',
    ].join(' '),

    // Use localStorage for persistent sessions across browser restarts
    userStore: new WebStorageStateStore({
      store: window.localStorage,
    }),

    // Automatically include user info in token response
    loadUserInfo: true,

    // Silent token renewal settings
    automaticSilentRenew: true,
    silentRequestTimeoutInSeconds: 10,

    // Token handling
    filterProtocolClaims: true,
    revokeTokensOnSignout: true,
  };
}

/**
 * UserManager instance for handling OIDC operations
 *
 * Usage:
 * - userManager.signinRedirect() - Start login flow
 * - userManager.signinRedirectCallback() - Handle callback after login
 * - userManager.signoutRedirect() - Start logout flow
 * - userManager.getUser() - Get current user from storage
 * - userManager.removeUser() - Clear user from storage
 */
let userManager: UserManager | null = null;
let config: ReturnType<typeof createConfig> | null = null;

export function getUserManager(): UserManager {
  if (typeof window === 'undefined') {
    throw new Error('UserManager can only be used in browser environment');
  }

  if (!userManager) {
    // Create config lazily on first access (client-side only)
    config = createConfig();

    if (!config.client_id) {
      console.warn(
        'Zitadel client ID not configured. Set VITE_ZITADEL_CLIENT_ID env var.',
      );
    }
    userManager = new UserManager(config);

    // Listen for token expiration events
    userManager.events.addAccessTokenExpiring(() => {
      console.log('Access token expiring, attempting silent renewal...');
    });

    userManager.events.addAccessTokenExpired(() => {
      console.log('Access token expired');
    });

    userManager.events.addSilentRenewError((error) => {
      console.error('Silent renew error:', error);
    });

    userManager.events.addUserLoaded((user) => {
      console.log('User loaded:', user.profile.sub);
    });

    userManager.events.addUserUnloaded(() => {
      console.log('User unloaded');
    });
  }

  return userManager;
}

/**
 * JWT Claims structure from Zitadel tokens
 *
 * Standard OIDC claims plus Zitadel-specific claims:
 * - urn:zitadel:iam:org:id - Organization ID
 * - urn:zitadel:iam:org:domain:primary - Organization primary domain
 * - urn:zitadel:iam:org:project:roles - Project roles (if configured)
 * - tenantSlug - Custom claim (from Zitadel Action)
 * - organizationSlug - Custom claim (from Zitadel Action)
 */
export interface ZitadelUserProfile {
  sub: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
  email?: string;
  email_verified?: boolean;
  locale?: string;

  // Zitadel organization claims
  'urn:zitadel:iam:org:id'?: string;
  'urn:zitadel:iam:org:domain:primary'?: string;
  'urn:zitadel:iam:org:project:roles'?: Record<string, Record<string, string>>;

  // Custom claims (set by Zitadel Actions)
  tenantSlug?: string;
  organizationSlug?: string;
}

/**
 * Get the Zitadel config (only available after getUserManager() is called)
 */
export function getZitadelConfig() {
  if (!config) {
    throw new Error(
      'Config not initialized. Call getUserManager() first (client-side only).',
    );
  }
  return config;
}
